import React from 'react';
import Cookies from 'js-cookie';
import { Hero, SuccessfulResult } from '../configs';
import { arrayEquals, shuffle, sortBy } from './nodash';
import { ENV } from '../constants';
import { Flow } from '../state/globalStateContext';

let pickedHero: Hero | null = null;
let lastResults: Array<SuccessfulResult> = [];
export function getHeroFromResults(results: Array<SuccessfulResult>): Hero {
  // Memoization
  if (pickedHero && arrayEquals(results, lastResults)) {
    return pickedHero;
  }
  const heroCounts: Partial<Record<Hero, number>> = {};
  for (const result of results) {
    if (result !== 'correct') {
      heroCounts[result] = (heroCounts[result] || 0) + 1;
    }
  }
  const heroCountsEntries = Object.entries(heroCounts) as Array<[Hero, number]>;
  const sortedHeroCountsEntries = sortBy(heroCountsEntries, ([, count]) => count, 'desc');
  const maxCount = sortedHeroCountsEntries[0][1];
  pickedHero = shuffle(
    sortedHeroCountsEntries.filter(heroCountEntry => heroCountEntry[1] === maxCount),
  )[0][0];
  lastResults = results;
  return pickedHero;
}

export const clsx = (...args: Array<string | false | null | undefined>): string =>
  args.filter(Boolean).join(' ');

export function setCookie(
  name: string,
  value: string,
  {
    expires = 365,
    sameSite = 'none',
    ...extraOptions
  }: {
    expires?: number;
    sameSite?: 'strict' | 'lax' | 'none';
  },
): void {
  const secure = location ? location.protocol === 'https:' : true;

  const cookieOptions = { expires, sameSite, secure, ...extraOptions };

  // Fallback for older browsers where can not set SameSite=None, SEE: https://web.dev/samesite-cookie-recipes/#handling-incompatible-clients
  if (sameSite === 'none') {
    Cookies.set(name + '-legacy', value, cookieOptions);
  }

  // set the regular cookie
  Cookies.set(name, value, cookieOptions);
}

export function getCookieValue(name: string): string | undefined {
  let cookieValue = Cookies.get(name);

  // if the cookieValue is undefined check for the legacy cookie
  if (cookieValue === undefined) {
    cookieValue = Cookies.get(name + '-legacy');
  }
  return cookieValue;
}

export function isDeviceMobile(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const mobileRegex = /Mobile|mini|Fennec|Android|iP(ad|od|hone)/;
  return (
    mobileRegex.test(window.navigator.appVersion) || mobileRegex.test(window.navigator.userAgent)
  );
}

let eventsSent = 0;
export function sendEvent(
  flow: Flow | null,
  name: string,
  props?: Record<string, string | number | boolean | null>,
  forceSend?: boolean,
) {
  if (
    ENV === 'production' &&
    typeof window !== 'undefined' &&
    'plausible' in window &&
    (eventsSent < 20 || forceSend)
  ) {
    // @ts-ignore
    window.plausible(name, {
      props: {
        flow,
        ...(props || {}),
      },
    });
    eventsSent++;
  }
}

export function replaceNewLinesWithBr(
  str: string,
  preprocessor: (
    str: string,
  ) => Array<React.ReactElement | string> | React.ReactElement | string = (str: string) => str,
): React.ReactNode {
  const splitStr = str.split('\n');
  const splitStrWithBrs: Array<string | React.ReactNode> = [];
  for (let i = 0; i < splitStr.length; i++) {
    splitStrWithBrs.push(preprocessor ? preprocessor(splitStr[i]) : splitStr[i]);
    splitStrWithBrs.push(<br key={i}></br>);
  }
  splitStrWithBrs.pop();
  return <>{splitStrWithBrs}</>;
}

export function replaceNewLinesWithH3(
  str: string,
  preprocessor: (str: string) => React.ReactNode = (str: string) => str,
): React.ReactNode {
  const strParts = str.split('\n');
  return strParts.map((string, i) => <h3 key={i}>{preprocessor(string)}</h3>);
}

export function replaceNewLinesWithP(
  str: string,
  preprocessor: (str: string) => React.ReactNode = (str: string) => str,
): React.ReactNode {
  const strParts = str.split('\n');
  return strParts.map((string, i) => <p key={i}>{preprocessor(string)}</p>);
}

export function replaceAsteriskWithSpan(
  str: string,
  preprocessor: (str: string) => React.ReactNode = (str: string) => str,
): React.ReactNode {
  const strParts = str.split('*');
  return strParts.map((string, i) =>
    i % 2 !== 0 ? <span key={i}>{preprocessor(string)}</span> : preprocessor(string),
  );
}

export function replaceAsteriskWithEm(
  str: string,
  preprocessor: (str: string) => React.ReactNode = (str: string) => str,
): React.ReactNode {
  const strParts = str.split('*');
  return strParts.map((string, i) =>
    i % 2 !== 0 ? <em key={i}>{preprocessor(string)}</em> : preprocessor(string),
  );
}

export function replaceTripleTagsWithCode(
  str: string,
  preprocessor: (str: string) => React.ReactNode = (str: string) => str,
): React.ReactNode {
  const strParts = str.split(/<<<|>>>/g);
  return strParts.map((string, i) =>
    i % 2 !== 0 ? <code key={i}>{preprocessor(string)}</code> : preprocessor(string),
  );
}
